import { Link } from "react-router-dom";
import { Wrench, ShieldCheck, CalendarDays, Wallet, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Wrench, title: "Carnet numérique", text: "Toutes vos opérations d'entretien réunies, avec factures et photos." },
  { icon: CalendarDays, title: "Échéances claires", text: "Vous fixez vos échéances, nous vous montrons ce qui arrive." },
  { icon: Wallet, title: "Budget maîtrisé", text: "Suivez vos dépenses par catégorie et par année." },
  { icon: ShieldCheck, title: "Vos données privées", text: "Chaque compte n'accède qu'à ses propres véhicules." },
];

export default function Landing() {
  const { user } = useAuth();
  const target = user ? "/app/vehicules?new=1" : "/inscription";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-blue-600 text-white grid place-items-center">
              <Wrench className="h-5 w-5" />
            </span>
            <span className="font-display font-bold text-lg text-slate-900">Mon Entretien Auto</span>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/app" data-testid="go-dashboard-btn" className="rounded-full bg-blue-600 text-white px-5 py-2 text-sm font-medium hover:bg-blue-700 transition-colors">
                Mon tableau de bord
              </Link>
            ) : (
              <>
                <Link to="/connexion" data-testid="header-login-link" className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  Connexion
                </Link>
                <Link to="/inscription" data-testid="header-register-link" className="rounded-full bg-blue-600 text-white px-5 py-2 text-sm font-medium hover:bg-blue-700 transition-colors">
                  Inscription
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-2 gap-14 items-center">
        <div className="animate-fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Carnet d'entretien numérique</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
            Mon Entretien Auto
          </h1>
          <p className="mt-5 text-lg md:text-xl text-slate-600 max-w-xl">
            Ne ratez plus jamais l'entretien de votre voiture.
          </p>
          <p className="mt-4 text-base text-slate-500 max-w-xl leading-relaxed">
            Enregistrez vos véhicules, votre kilométrage, vos interventions et vos prochaines
            échéances. Simple, rapide, pensé pour tout le monde.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link
              to={target}
              data-testid="add-vehicle-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white px-7 py-3.5 font-medium hover:bg-blue-700 hover:-translate-y-0.5 transition-transform shadow-sm"
            >
              Ajouter mon véhicule <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/connexion"
              data-testid="secondary-login-link"
              className="inline-flex items-center justify-center rounded-full bg-white border border-slate-200 px-7 py-3.5 font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              J'ai déjà un compte
            </Link>
          </div>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
          <img
            src="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjYXIlMjBtZWNoYW5pYyUyMGdhcmFnZSUyMHNlcnZpY2V8ZW58MHx8fHwxNzg2NDgzMjAwfDA&ixlib=rb-4.1.0&q=85"
            alt="Atelier mécanique"
            className="w-full h-[320px] md:h-[440px] object-cover rounded-2xl border border-slate-200 shadow-sm"
          />
          <div className="absolute -bottom-6 left-4 right-4 md:left-8 md:right-8 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Prochaine échéance</p>
            <div className="mt-2 flex items-center justify-between">
              <span className="font-display font-semibold text-slate-900">Vidange moteur</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border text-amber-700 bg-amber-50 border-amber-200">
                Bientôt
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 pb-24 pt-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:-translate-y-0.5 transition-transform animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <f.icon className="h-6 w-6 text-blue-600" />
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-800">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        Mon Entretien Auto — Votre carnet d'entretien, toujours à jour.
      </footer>
    </div>
  );
}
